/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "components/Input";
import { useAppContext } from "contexts";
import React, { useEffect, useMemo, useState } from "react";
import Button from "components/Button";
import { FormProvider, useForm } from "react-hook-form";
import Select from "react-select";
import { AlertTriangle } from "lucide-react";
import apiService from "services/apiService";
import { LeavePayload } from "services/Leave/leaveInterface";
import leaveManager from "services/Leave/leaveManager";
import { getMonthByDate, getYear } from "util/Util";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";
import * as yup from "yup";
import { customSelectStyles } from "util/selectStyles";

interface LeaveBalance {
  id: number;
  userId: number;
  leavePolicyId: number;
  leaveType: string;
  balance: number;
  lastAccrualDate: string;
  month: number;
  year: number;
}

// Clean day formatting: "1", "8", "0.5", "1.5" (trailing zeros trimmed).
const formatDays = (value: number): string => String(parseFloat((Number(value) || 0).toFixed(2)));

const validationSchema = yup.object({
  leaveFrom: yup.string().required("Leave From is required"),
  leaveTo: yup.string().required("Leave To is required"),
  leaveFor: yup.string().required("Leave For is required"),
  days: yup.string(),
  leaveType: yup.string().required("Leave Type is required"),
  reason: validateNoTrailingOrLeadingWhitespace("Leave Reason").required("Leave Reason is required"),
});

interface IProps {
  onSuccess: () => void;
  onError: (errorMessage?: string) => void; // Updated to accept an error message
  onClose: () => void;
  // Optional preselected leave type (e.g. when launched from a leave-balance
  // card). Resolved against the available options; falls back to the default.
  defaultLeaveType?: string;
}

// Resolve an external leave-type hint (which may be a policy/balance name like
// "Sick") to one of the form's option values (like "Sick leave").
const resolveLeaveType = (
  hint: string | undefined,
  options: { value: string; label: string }[],
): string => {
  const fallback = options[0]?.value ?? "Sick leave";
  if (!hint) return fallback;
  const h = hint.trim().toLowerCase();
  const match = options.find(
    (o) =>
      o.value.toLowerCase() === h ||
      o.label.toLowerCase() === h ||
      o.value.toLowerCase().includes(h) ||
      h.includes(o.value.toLowerCase()),
  );
  return match?.value ?? fallback;
};

const leaveForOptions = [
  { value: 1, label: "Full Day" },
  { value: 0.5, label: "Half Day" },
];

const leaveTypeOptions = [
  { value: "Sick leave", label: "Sick Leave" },
  { value: "Urgent leave", label: "Urgent leave" },
  {
    value: "Maternity and paternity leave",
    label: "Maternity and paternity leave",
  },
  {
    value: "Public and religious holidays",
    label: "Public and religious holidays",
  },
  { value: "Bereavement leave", label: "Bereavement leave" },
  { value: "Time off in lieu (TOIL)", label: "Time off in lieu (TOIL)" },
  { value: "Unpaid leave", label: "Unpaid leave" },
  { value: "Paid leave", label: "Paid leave" },
  { value: "Other leave", label: "Other leave" },
];

const AddLeave = ({ onSuccess, onError, onClose, defaultLeaveType }: IProps) => {
  const initialLeaveType = resolveLeaveType(defaultLeaveType, leaveTypeOptions);
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      leaveFrom: "",
      leaveTo: "",
      leaveFor: JSON.stringify(leaveForOptions[0].value),
      days: "0",
      leaveType: initialLeaveType,
      reason: ""
    }
  });
  const { isDirty } = methods.formState;
  const [leaveFrom, setLeaveFrom] = useState("");
  const [leaveTo, setLeaveTo] = useState("");
  const [days, setDays] = useState(0);
  const [selectedLeaveFor, setSelectedLeaveFor] = useState(1);
  const [selectedLeaveType, setSelectedLeaveType] = useState(initialLeaveType);
  const { user } = useAppContext();
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loadingBalances, setLoadingBalances] = useState(false);

  // Leave balance for the current month/year → drives the leave-type options
  // and the over-balance warning.
  useEffect(() => {
    const today = new Date();
    setLoadingBalances(true);
    apiService
      .get(`api/leave-balance/me?month=${today.getMonth() + 1}&year=${today.getFullYear()}`)
      .then((res) => setBalances(Array.isArray(res.data) ? res.data : []))
      .catch((error) => {
        console.log("Failed to fetch leave balance:", error);
        setBalances([]);
      })
      .finally(() => setLoadingBalances(false));
  }, []);

  // Leave-type options come from the balance response; fall back to the static
  // list only when no balance is available.
  const balanceOptions = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>();
    balances.forEach((b) => {
      if (b.leaveType && !map.has(b.leaveType)) {
        map.set(b.leaveType, { value: b.leaveType, label: b.leaveType });
      }
    });
    return Array.from(map.values());
  }, [balances]);

  const leaveTypeSelectOptions = balanceOptions.length ? balanceOptions : leaveTypeOptions;

  // Once balances load, make sure the selected type is a valid option.
  useEffect(() => {
    if (!balanceOptions.length) return;
    setSelectedLeaveType((prev) => {
      const resolved = resolveLeaveType(defaultLeaveType ?? prev, balanceOptions);
      methods.setValue("leaveType", resolved);
      return resolved;
    });
  }, [balanceOptions, defaultLeaveType, methods]);

  // Available balance for the selected type + over-limit flag (non-blocking).
  const selectedBalance = balances.find((b) => b.leaveType === selectedLeaveType);
  const availableDays = selectedBalance ? Number(selectedBalance.balance) : null;
  const overLimit = availableDays != null && days > availableDays;

  const onSubmit = async (data: any) => {
    const payload: LeavePayload = {
      leaveTo: data.leaveTo,
      leaveFrom: data.leaveFrom,
      unit: days,
      reason: data.reason.trim(),
      leaveType: `${selectedLeaveType}`,
      status: "Submitted",
      department: "development",
      comment: "",
      year: getYear(),
      month: getMonthByDate(data.leaveTo),
      email: user.email,
      leaveAppliedDate: Date.now(),
    };

    leaveManager
      .addLeave(payload)
      .then((res: any) => {
        if (res.status === 200) {
          onSuccess();
        } else {
          // Extract error message from successful but non-200 responses if backend sends it this way
          const errorMessage = res.data?.message || res.data?.error || "Something went wrong";
          onError(errorMessage);
        }
      })
      .catch((err: any) => {
        console.log(err);
        // Extract error message from the Axios error object
        const errorMessage =
          err?.response?.data ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.response?.data ||
          err?.message ||
          "Something went wrong";

        onError(errorMessage);
      });
  };

  const onLeaveForChanged = (selectedOption: any) => {
    setSelectedLeaveFor(selectedOption.value);
    methods.clearErrors("leaveFor");
  };

  const onLeaveFromChanged = (e: any) => {
    setLeaveFrom(e.target.value);
  };

  const onLeaveToChanged = (e: any) => {
    setLeaveTo(e.target.value);
  };

  const onLeaveTypeChanged = (selectedOption: any) => {
    setSelectedLeaveType(selectedOption.value);
    methods.clearErrors("leaveType");
  };

  const onDaysChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = parseFloat(e.target.value);
    setDays(value);
  };

  // Inclusive calendar-day count between the two dates. Order-independent
  // (either date can be picked first), so From==To → 1 day, From..From+1 → 2.
  const calculateDays = (start: string, end: string) => {
    if (!start || !end) return 0;

    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0;

    // Normalise to midnight and to the correct order so the diff is exact.
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const from = Math.min(startDate.getTime(), endDate.getTime());
    const to = Math.max(startDate.getTime(), endDate.getTime());

    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    return Math.round((to - from) / MS_PER_DAY) + 1; // +1 → both ends inclusive
  };

  useEffect(() => {
    if (leaveFrom && leaveTo) {
      let totalDays = calculateDays(leaveFrom, leaveTo);

      if (selectedLeaveFor === 0.5) {
        totalDays = 0.5;
      }

      setDays(totalDays);
    }
  }, [leaveFrom, leaveTo, selectedLeaveFor]);

  const minLeaveToDate = leaveFrom
    ? new Date(leaveFrom).toISOString().split("T")[0]
    : "";
  const maxLeaveToDate = leaveTo
    ? new Date(leaveTo).toISOString().split("T")[0]
    : "";

  useEffect(() => {
    methods.setValue("leaveFor", JSON.stringify(leaveForOptions[0].value));
  }, [methods]);

  const fromDate = methods.getValues("leaveFrom");
  const toDate = methods.getValues("leaveTo");

  return (
    <div className="space-y-1">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="px-4 pt-2 pb-3"
        >
          {/* Updated gap-y-2 to gap-y-1 to reduce vertical spacing */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            <Input
              compact
              name="leaveFrom"
              type="date"
              required
              label="Leave From"
              onChange={onLeaveFromChanged}
              value={leaveFrom}
              max={maxLeaveToDate}
            />

            <div>
              <label className="flex items-center justify-between gap-2 mb-1 text-sm font-semibold">
                <span>Leave Type <span className="text-error">*</span></span>
                {availableDays != null && (
                  <span
                    className={`text-xs font-normal ${overLimit
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-slate-500 dark:text-slate-400"}`}
                  >
                    {formatDays(availableDays)} {availableDays === 1 ? "day" : "days"} left
                  </span>
                )}
              </label>
              <Select
                styles={customSelectStyles}
                options={leaveTypeSelectOptions}
                isLoading={loadingBalances}
                {...methods.register("leaveType")}
                onChange={onLeaveTypeChanged}
                value={leaveTypeSelectOptions.find(
                  (op) => op.value === selectedLeaveType
                )}
              />
            </div>

            <Input
              compact
              type="date"
              label="Leave To"
              name="leaveTo"
              required
              onChange={onLeaveToChanged}
              value={leaveTo}
              min={minLeaveToDate}
            />

            {/* Added compact and updated classes to match border-radius and background */}
            <Input
              compact
              type="number"
              label="Days"
              name="days"
              value={days}
              onChange={onDaysChange}
              required
              disabled={true}
              className="!rounded-lg !bg-gray-50 dark:!bg-gray-700 !border-gray-300 dark:!border-gray-600 !text-gray-900 dark:!text-white disabled:!cursor-not-allowed"
            />

            <div>
              <label className="block mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                Leave For <span className="text-error">*</span>
              </label>
              <Select
                options={
                  fromDate === toDate
                    ? leaveForOptions
                    : [{ value: 1, label: "Full Day" }]
                }
                {...methods.register("leaveFor")}
                onChange={onLeaveForChanged}
                value={leaveForOptions.find(
                  (op) => op.value === selectedLeaveFor
                )}
                styles={customSelectStyles}
              />
            </div>
          </div>

          {/* Over-balance warning — non-blocking. Fixed min-height reserves the
              space so the form layout never jumps when it toggles. */}
          <div className="mt-1 min-h-[52px]">
            {overLimit && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  You're applying <span className="font-semibold">{formatDays(days)}</span>{" "}
                  {days === 1 ? "day" : "days"}, but only{" "}
                  <span className="font-semibold">{formatDays(availableDays ?? 0)}</span>{" "}
                  {availableDays === 1 ? "day is" : "days are"} available for{" "}
                  <span className="font-semibold">{selectedLeaveType}</span>. You can still submit.
                </span>
              </div>
            )}
          </div>

          <div className="mt-1">
            <Input
              compact
              type="textarea"
              label="Leave Reason"
              placeholder="Enter your reason..."
              name="reason"
              required
            />
          </div>

          <div className="mt-3 flex justify-end gap-2">
            <Button
              onClick={onClose}
              type="button"
              className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={!isDirty}
              className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
            >
              Submit
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddLeave;